import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Book, Loader2, Eye, User, X, LogIn, LogOut, ScanLine } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePublishedStorybooks, PublishedStorybook } from '../hooks/usePublishedStorybooks';
import { useAuth } from '../../../contexts/AuthContext';

export const StorybookLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { storybooks, loading, error, viewingAs, viewingAsName } = usePublishedStorybooks();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading storybooks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Book className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-600 mb-2">Failed to load storybooks</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Admin Preview Banner */}
      {viewingAs && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <Eye className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-medium text-sm">Admin Preview Mode</p>
                  <p className="text-amber-100 text-xs flex items-center gap-1">
                    <User className="w-3 h-3" />
                    Viewing as: <strong>{viewingAsName}</strong>
                    <span className="mx-1">•</span>
                    {storybooks.length} storybook{storybooks.length !== 1 ? 's' : ''} accessible
                  </p>
                </div>
              </div>
              <a
                href="/storybooks"
                className="flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
              >
                <X className="w-4 h-4" />
                Exit Preview
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header - Clean design without search/filter */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/turn-2.png" 
                alt="Turn Logo" 
                className="h-12 w-auto object-contain"
              />
              <h1 className="text-2xl font-bold text-gray-900">Your Turn Library</h1>
            </div>
            
            {/* Auth Buttons */}
            {!viewingAs && (
              <div className="flex items-center gap-3">
                {user ? (
                  <>
                    <div className="hidden sm:block text-right">
                      <div className="text-sm font-medium text-gray-700">
                        {profile?.name || user.email}
                      </div>
                      <div className="text-xs text-gray-500">Parent</div>
                    </div>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="hidden sm:inline font-medium">Sign Out</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    <span className="hidden sm:inline font-medium">Sign In</span>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="mt-4 flex gap-2">
            <Link
              to="/storybooks"
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-medium"
            >
              Storybooks
            </Link>
            <Link
              to="/flashcards"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors font-medium"
            >
              Flashcards
            </Link>
          </div>
        </div>
      </header>

      {/* Storybook Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Scan a Book - Coming Soon Card */}
          {user && !viewingAs && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
            >
              <div className="block h-full cursor-not-allowed">
                <div className="bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-2xl overflow-hidden shadow-lg h-full relative opacity-75">
                  {/* Coming Soon Badge */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      Coming Soon
                    </span>
                  </div>
                  
                  {/* Subtle background pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-4 right-4 w-32 h-32 bg-white/20 rounded-full blur-2xl" />
                    <div className="absolute bottom-8 left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
                  </div>
                  
                  {/* Cover area */}
                  <div className="aspect-[4/3] relative overflow-hidden flex items-center justify-center">
                    <div className="relative">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                        <ScanLine className="w-10 h-10 text-white/70" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 relative">
                    <h3 className="font-bold text-lg text-white/90 mb-1 flex items-center gap-2">
                      Scan a Book
                    </h3>
                    
                    <p className="text-gray-200 text-sm mb-3">
                      Turn any book into a Turn Color experience
                    </p>
                    
                    {/* Status */}
                    <div className="flex items-center justify-end">
                      <span className="text-white/70 font-medium text-sm inline-flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-lg">
                        In Development
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Existing storybooks */}
          {storybooks.map((book, index) => (
            <StorybookCard key={book.id} book={book} index={user && !viewingAs ? index + 1 : index} />
          ))}
        </div>

        {/* Empty state only if no storybooks AND not logged in (can't scan) */}
        {storybooks.length === 0 && !user && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Book className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-700 mb-2">No storybooks found</h3>
            <p className="text-gray-500 mb-4">Sign in to scan your own books</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              <LogIn className="w-5 h-5" />
              Sign In
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

interface StorybookCardProps {
  book: PublishedStorybook;
  index: number;
}

// Format date as "December 2, 2025"
const formatPublishedDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

const StorybookCard: React.FC<StorybookCardProps> = ({ book, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/storybooks/${book.slug}`}
        className="block group"
      >
        <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
          {/* Cover Image */}
          <div className="aspect-[4/3] bg-gradient-to-br from-blue-100 to-indigo-100 relative overflow-hidden">
            {book.coverImageUrl ? (
              <img
                src={book.coverImageUrl}
                alt={book.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Book className="w-16 h-16 text-blue-300" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-5">
            <h3 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-1">
              {book.title}
            </h3>
            
            {/* Date Published */}
            <p className="text-xs text-gray-400 mb-2">
              {formatPublishedDate(book.createdAt)}
            </p>
            
            {book.description && (
              <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                {book.description}
              </p>
            )}
            
            {/* Read Button */}
            <div className="flex items-center justify-end">
              <span className="text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                Read Now
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default StorybookLibrary;
